import React from 'react';
import { Star, Shield } from 'lucide-react';

export type BiblicalRankTierName =
  | 'Seeker'
  | 'Disciple'
  | 'Believer'
  | 'Witness'
  | 'Servant'
  | 'Minister'
  | 'Elder'
  | 'Shepherd'
  | 'Apostle'
  | 'Overcomer';

export interface ClientRankTierMeta {
  tierIndex: number;
  name: BiblicalRankTierName;
  scriptureTitle: string;
  verseRef: string;
  divisions: string[];
  starsPerDivision: number;
  minTotalStars: number;
  accentColor: string;
  secondaryColor: string;
  description: string;
}

export const BIBLICAL_RANK_TIERS_CLIENT: ClientRankTierMeta[] = [
  {
    tierIndex: 1,
    name: 'Seeker',
    scriptureTitle: 'The Diligent Seeker',
    verseRef: 'Matthew 7:7',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 3,
    minTotalStars: 0,
    accentColor: '#94A3B8',
    secondaryColor: '#334155',
    description: 'Begin your competitive Scripture journey by seeking wisdom and truth.',
  },
  {
    tierIndex: 2,
    name: 'Disciple',
    scriptureTitle: 'The Steadfast Learner',
    verseRef: 'John 8:31',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 3,
    minTotalStars: 9,
    accentColor: '#38BDF8',
    secondaryColor: '#0369A1',
    description: 'Abiding in the Word with disciplined daily study and growing accuracy.',
  },
  {
    tierIndex: 3,
    name: 'Believer',
    scriptureTitle: 'The Rooted in Faith',
    verseRef: 'Romans 1:16',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 3,
    minTotalStars: 18,
    accentColor: '#10B981',
    secondaryColor: '#047857',
    description: 'Grounded in foundational Old and New Testament truth.',
  },
  {
    tierIndex: 4,
    name: 'Witness',
    scriptureTitle: 'The Bold Messenger',
    verseRef: 'Acts 1:8',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 4,
    minTotalStars: 27,
    accentColor: '#14B8A6',
    secondaryColor: '#0F766E',
    description: 'Testifying accurately across Gospels, Acts, and prophetic history.',
  },
  {
    tierIndex: 5,
    name: 'Servant',
    scriptureTitle: 'The Faithful Steward',
    verseRef: 'Matthew 25:21',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 4,
    minTotalStars: 39,
    accentColor: '#A855F7',
    secondaryColor: '#6B21A8',
    description: 'Rightly dividing the Word of truth under timed competitive pressure.',
  },
  {
    tierIndex: 6,
    name: 'Minister',
    scriptureTitle: 'The Equipped Workman',
    verseRef: '2 Timothy 2:15',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 4,
    minTotalStars: 51,
    accentColor: '#EC4899',
    secondaryColor: '#9D174D',
    description: 'Deep command of Pauline Epistles, covenant theology, and wisdom books.',
  },
  {
    tierIndex: 7,
    name: 'Elder',
    scriptureTitle: 'The Pillar of Counsel',
    verseRef: 'Titus 1:9',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 5,
    minTotalStars: 63,
    accentColor: '#F59E0B',
    secondaryColor: '#B45309',
    description: 'Holding fast the faithful Word with high-precision mastery.',
  },
  {
    tierIndex: 8,
    name: 'Shepherd',
    scriptureTitle: 'The Guardian of the Flock',
    verseRef: '1 Peter 5:2',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 5,
    minTotalStars: 78,
    accentColor: '#F97316',
    secondaryColor: '#C2410C',
    description: 'Guiding the fellowship with encyclopedic scriptural recall.',
  },
  {
    tierIndex: 9,
    name: 'Apostle',
    scriptureTitle: 'The Commissioned Envoy',
    verseRef: 'Ephesians 2:20',
    divisions: ['III', 'II', 'I'],
    starsPerDivision: 5,
    minTotalStars: 93,
    accentColor: '#EF4444',
    secondaryColor: '#991B1B',
    description: 'Elite mastery across all 66 canonical books and difficult exegesis.',
  },
  {
    tierIndex: 10,
    name: 'Overcomer',
    scriptureTitle: 'The Crowned Conqueror',
    verseRef: 'Revelation 3:21',
    divisions: ['I'],
    starsPerDivision: 5,
    minTotalStars: 108,
    accentColor: '#FACC15',
    secondaryColor: '#CA8A04',
    description: 'Supreme Biblical Rank achieved by the highest echelon of global scholars.',
  },
];

export const BIBLICAL_RANKS_META = BIBLICAL_RANK_TIERS_CLIENT.map((t) => ({
  ...t,
  index: t.tierIndex,
  theme: `${t.scriptureTitle} (${t.verseRef}) — ${t.description}`,
}));

export function getBiblicalTierMeta(tierNameOrIndex?: string | number): ClientRankTierMeta {
  if (typeof tierNameOrIndex === 'number') {
    return (
      BIBLICAL_RANK_TIERS_CLIENT.find((t) => t.tierIndex === tierNameOrIndex) ||
      BIBLICAL_RANK_TIERS_CLIENT[0]
    );
  }
  const clean = String(tierNameOrIndex || 'Seeker').trim().toLowerCase();
  return (
    BIBLICAL_RANK_TIERS_CLIENT.find(
      (t) => clean.startsWith(t.name.toLowerCase())
    ) || BIBLICAL_RANK_TIERS_CLIENT[0]
  );
}

export interface BiblicalRankBadgeProps {
  tierName?: string;
  rankName?: string;
  division?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showLabel?: boolean;
  className?: string;
}

export const BiblicalRankBadge: React.FC<BiblicalRankBadgeProps> = ({
  tierName,
  rankName,
  division = 'III',
  size = 'md',
  showLabel = false,
  className = '',
}) => {
  const meta = getBiblicalTierMeta(rankName || tierName || 'Seeker');
  const dim =
    size === 'sm'
      ? 36
      : size === 'md'
      ? 56
      : size === 'lg'
      ? 84
      : 112;

  const gradId = `qb-rank-grad-${meta.tierIndex}-${size}`;
  const glowId = `qb-rank-glow-${meta.tierIndex}-${size}`;

  return (
    <div
      className={`inline-flex flex-col items-center justify-center select-none ${className}`}
      data-testid={`biblical-rank-badge-${meta.name.toLowerCase()}`}
    >
      <div className="relative flex items-center justify-center">
        <svg
          width={dim}
          height={dim}
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="drop-shadow-[0_6px_16px_rgba(0,0,0,0.55)]"
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={meta.accentColor} />
              <stop offset="100%" stopColor={meta.secondaryColor} />
            </linearGradient>
            <radialGradient id={glowId} cx="50%" cy="40%" r="55%">
              <stop offset="0%" stopColor={meta.accentColor} stopOpacity="0.45" />
              <stop offset="100%" stopColor={meta.secondaryColor} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Ambient Aura */}
          <circle cx="50" cy="50" r="46" fill={`url(#${glowId})`} />

          {/* Ornate Wing / Laurel Frame for Higher Tiers */}
          {meta.tierIndex >= 7 && (
            <path
              d="M14 54 C6 40, 12 22, 28 16 C22 28, 22 42, 26 54 Z M86 54 C94 40, 88 22, 72 16 C78 28, 78 42, 74 54 Z"
              fill={`url(#${gradId})`}
              opacity="0.85"
            />
          )}

          {/* Outer Crest Shield */}
          <path
            d="M50 8 L84 22 L84 52 C84 74 68 88 50 94 C32 88 16 74 16 52 L16 22 Z"
            fill="#111827"
            stroke={`url(#${gradId})`}
            strokeWidth={meta.tierIndex >= 8 ? '5' : '4'}
          />

          {/* Inner Metallic Shield */}
          <path
            d="M50 15 L77 26 L77 51 C77 69 64 81 50 86 C36 81 23 69 23 51 L23 26 Z"
            fill={`url(#${gradId})`}
            fillOpacity="0.22"
            stroke={meta.accentColor}
            strokeWidth="1.5"
            strokeOpacity="0.6"
          />

          {/* Tier-Specific Central Biblical Emblem */}
          {meta.tierIndex <= 3 && (
            /* Open Scripture & Radiant Star (Seeker, Disciple, Believer) */
            <g>
              <path
                d="M33 42 C39 39, 46 40, 50 44 C54 40, 61 39, 67 42 L67 62 C61 59, 54 60, 50 64 C46 60, 39 59, 33 62 Z"
                fill={meta.accentColor}
                opacity="0.9"
              />
              <path d="M50 30 L52.5 36 L59 36.5 L54 40.5 L55.5 47 L50 43.5 L44.5 47 L46 40.5 L41 36.5 L47.5 36 Z" fill="#FAFAFA" />
            </g>
          )}

          {meta.tierIndex >= 4 && meta.tierIndex <= 6 && (
            /* Torch of Witness & Crossed Scroll / Sword of the Spirit (Witness, Servant, Minister) */
            <g>
              <path
                d="M50 24 C56 32, 58 39, 50 46 C42 39, 44 32, 50 24 Z"
                fill="#FDE047"
              />
              <rect x="47" y="45" width="6" height="22" rx="3" fill={meta.accentColor} />
              <path
                d="M34 52 L66 52"
                stroke="#FAFAFA"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </g>
          )}

          {meta.tierIndex >= 7 && meta.tierIndex <= 9 && (
            /* Crowned Menorah / Shepherd Staff / Apostolic Star (Elder, Shepherd, Apostle) */
            <g>
              <path
                d="M34 38 L40 28 L50 35 L60 28 L66 38 L62 46 L38 46 Z"
                fill="#FDE047"
              />
              <path
                d="M50 48 L53.5 55.5 L62 56.5 L55.5 62 L57.5 70 L50 65.5 L42.5 70 L44.5 62 L38 56.5 L46.5 55.5 Z"
                fill="#FAFAFA"
              />
            </g>
          )}

          {meta.tierIndex === 10 && (
            /* Supreme Overcomer Celestial Crown & Radiant Cross */
            <g>
              <path
                d="M30 40 L37 25 L50 34 L63 25 L70 40 L65 50 L35 50 Z"
                fill="#FACC15"
                stroke="#FEF08A"
                strokeWidth="1.5"
              />
              <path
                d="M50 52 L50 74 M41 61 L59 61"
                stroke="#FFFFFF"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </g>
          )}

          {/* Division Roman Numeral Ribbon at Bottom */}
          <rect
            x="33"
            y="72"
            width="34"
            height="15"
            rx="7.5"
            fill="#090D16"
            stroke={meta.accentColor}
            strokeWidth="1.8"
          />
          <text
            x="50"
            y="82.5"
            textAnchor="middle"
            fill="#FAFAFA"
            fontSize="10"
            fontWeight="800"
            fontFamily="monospace"
          >
            {meta.name === 'Overcomer' ? 'MAX' : division}
          </text>
        </svg>
      </div>

      {showLabel && (
        <div className="mt-1.5 text-center">
          <div
            className="text-xs sm:text-sm font-extrabold tracking-tight"
            style={{ color: meta.accentColor }}
          >
            {meta.name} {meta.name === 'Overcomer' ? '' : division}
          </div>
          <div className="text-[10px] text-[#A3A3A3]">{meta.scriptureTitle}</div>
        </div>
      )}
    </div>
  );
};

export const RankStarsRow: React.FC<{
  stars: number;
  maxStars: number;
  size?: 'sm' | 'md' | 'lg';
}> = ({ stars, maxStars, size = 'md' }) => {
  const starSize =
    size === 'sm' ? 'w-3.5 h-3.5' : size === 'lg' ? 'w-6 h-6' : 'w-4.5 h-4.5';
  const totalSlots = Math.max(3, maxStars);

  return (
    <div
      className="inline-flex items-center gap-1.5"
      data-testid="rank-stars-row"
      aria-label={`${stars} of ${totalSlots} rank stars`}
    >
      {Array.from({ length: totalSlots }).map((_, idx) => {
        const filled = idx < stars;
        return (
          <Star
            key={idx}
            className={`${starSize} transition-transform duration-300 ${
              filled
                ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.65)] scale-105'
                : 'text-[#404040] fill-[#1C1C1C]'
            }`}
          />
        );
      })}
      {stars > totalSlots && (
        <span className="ml-1 text-xs font-mono font-extrabold text-amber-300">
          +{stars - totalSlots}
        </span>
      )}
    </div>
  );
};

export const ProtectionShieldBar: React.FC<{
  protectionPoints?: number;
  points?: number;
}> = ({ protectionPoints, points }) => {
  const resolvedPoints = Number(protectionPoints ?? points ?? 0);
  const pct = Math.min(100, Math.max(0, Math.round(resolvedPoints)));
  const shieldReady = resolvedPoints >= 100;

  return (
    <div className="w-full" data-testid="rank-protection-bar">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="inline-flex items-center gap-1 font-semibold text-[#D4D4D4]">
          <Shield
            className={`w-3.5 h-3.5 ${
              shieldReady ? 'text-emerald-400 fill-emerald-400/20' : 'text-sky-400'
            }`}
          />
          <span>Star Protection Shield</span>
        </span>
        <span className="font-mono font-bold tabular-nums text-[#FAFAFA]">
          {resolvedPoints}/100 {shieldReady ? '(SHIELD ACTIVE)' : ''}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-[#1C1C1C] border border-[#262626] overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${
            shieldReady
              ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
              : 'bg-gradient-to-r from-sky-500 to-indigo-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
